/**
 * @fileoverview "Possibly in external administration" prompt for a company
 * contact.
 *
 * Purpose: surface the suggest-only EXAD hint that ingestion derived from the
 * corporate register (a liquidator / administrator listed against a related
 * entity on its ASIC search) so the practitioner can confirm or dismiss it.
 * Nothing is written to the authoritative `externalAdmin*` fields until they
 * click Confirm — that is the customer's chosen model, and it also keeps
 * appointees out of the company's structure roles until a human agrees.
 *
 * Confirm records the suggested status, links the first appointee that
 * already exists as a job contact, and copies their parsed title. Appointees
 * that aren't contacts yet are listed so the user knows to add them.
 *
 * @component
 * @example
 * <ExternalAdminSuggestionBanner
 *   companyContactId={contact._id}
 *   suggestion={contact.externalAdminSuggestion}
 *   onApplied={(next) => resetDraft(next)}
 * />
 */

import { useState } from "react";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Building2, Sparkles, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/ui/primitives/button";
import { extractErrorMessage } from "@/utils/errors";
import type { ExternalAdminSuggestion } from "./types";

/**
 * Values written on Confirm, in the shape of the section's text draft
 * (declared here rather than imported to avoid a module cycle with
 * `ExternalAdministrationSection`, which renders this banner).
 */
type AppliedExternalAdmin = { status: string; appointeeTitle: string };

/** Status used on Confirm when the source named an appointee but no status. */
const FALLBACK_STATUS = "In External Administration";

interface ExternalAdminSuggestionBannerProps {
  companyContactId: Id<"jobContacts">;
  suggestion: ExternalAdminSuggestion;
  disabled?: boolean;
  /**
   * Fired after Confirm succeeds with the values now on the server, so the
   * parent can mirror them into its autosave draft without a second save.
   */
  onApplied?: (next: AppliedExternalAdmin) => void;
}

export function ExternalAdminSuggestionBanner({
  companyContactId,
  suggestion,
  disabled,
  onApplied,
}: ExternalAdminSuggestionBannerProps) {
  const accept = useConvexMutation(
    api.contacts.mutations.acceptExternalAdminSuggestion,
  );
  const dismiss = useConvexMutation(
    api.contacts.mutations.dismissExternalAdminSuggestion,
  );
  const [isBusy, setIsBusy] = useState(false);

  const linkable = suggestion.appointees.find((a) => a.contactId);
  const status = suggestion.status ?? FALLBACK_STATUS;

  const handleConfirm = async () => {
    setIsBusy(true);
    try {
      await accept({
        companyContactId,
        status,
        appointeeContactId: linkable?.contactId,
        appointeeTitle: linkable?.title,
      });
      onApplied?.({ status, appointeeTitle: linkable?.title ?? "" });
      toast.success(
        linkable
          ? `Recorded ${status} and linked ${linkable.name} as appointee`
          : `Recorded ${status}`,
      );
    } catch (err) {
      toast.error(extractErrorMessage(err, "Something went wrong"));
    } finally {
      setIsBusy(false);
    }
  };

  const handleDismiss = async () => {
    setIsBusy(true);
    try {
      await dismiss({ companyContactId });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Something went wrong"));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div
      role="status"
      className="flex flex-col gap-2 rounded-lg border border-amber-300/70 bg-amber-50/60 p-3 dark:border-amber-500/40 dark:bg-amber-950/30"
    >
      <div className="flex items-center gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
        <Sparkles className="h-4 w-4 shrink-0" />
        Possibly in external administration
      </div>
      <p className="text-xs text-amber-900/80 dark:text-amber-200/80">
        Source documents suggest this company is{" "}
        <span className="font-medium">{status.toLowerCase()}</span>
        {suggestion.appointees.length > 0
          ? " with the following appointee(s):"
          : "."}
      </p>
      {suggestion.appointees.length > 0 && (
        <ul className="flex flex-col gap-1">
          {suggestion.appointees.map((a) => (
            <li
              key={a.name}
              className="flex items-center gap-2 text-xs text-amber-950 dark:text-amber-100"
            >
              {a.contactType === "person" ? (
                <UserRound className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              ) : (
                <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              )}
              <span className="font-medium">{a.name}</span>
              {a.title && (
                <span className="text-muted-foreground">{a.title}</span>
              )}
              {!a.contactId && (
                <span className="text-[11px] text-muted-foreground/80">
                  — not yet a contact on this job
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2 pt-1">
        <Button
          size="sm"
          className="h-7 text-xs"
          disabled={disabled || isBusy}
          onClick={handleConfirm}
        >
          Confirm
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 text-xs"
          disabled={disabled || isBusy}
          onClick={handleDismiss}
        >
          Not in external administration
        </Button>
      </div>
    </div>
  );
}
